import { useCallback, useContext, useEffect, useRef, useState } from "react";

import { AuthContext } from "~/auth/AuthProvider";
import setupFiles, {
	type LoadedUpload,
	type OutsideUpload,
	type OutsideUploadView,
	type PendingSetupRegistration,
	type SetupAttachment,
	type SetupFileChip
} from "~/helpers/aiChat/setupFiles";
import wsId from "~/helpers/wsId";
import useOnboardingJourneyMutations from "~/hooks/mutations/useOnboardingJourneyMutations";
import useOnboardingStanding from "~/hooks/useOnboardingStanding";
import { useGetOnboardingJourneyFilesQuery } from "~/services/endpoints/apiEndpoints";

const {
	afterRegistration,
	buildSetupAttachment,
	buildSetupFilesView,
	describeUploadFailure,
	forgetPendingRegistration,
	isReplayableRegistrationOutcome,
	loadPendingRegistrations,
	registrationsToReplay,
	rememberPendingRegistration,
	buildUnrepresentedImportedUploads,
	sourceKey,
	withAttachment
} = setupFiles;

export interface UseFernieSetupFilesOptions {
	isActive: boolean;
	onJourneyStarted: () => void;
	sessionId: string | null;
}

export interface UseFernieSetupFilesReturn {
	acceptsSetupFiles: boolean;
	attachFiles: (files: File[]) => Promise<void>;
	chips: SetupFileChip[];
	dismissAttachment: (id: string) => void;
	isJourneyConversation: boolean;
	isResolvingJourney: boolean;
	isSetupConversation: boolean;
	journeyId: string | null;
	loadedUploads: LoadedUpload[];
	outsideUploads: OutsideUploadView[];
	prepareOutsideUpload: (upload: OutsideUpload) => void;
	retryAttachment: (id: string) => Promise<void>;
}

const useFernieSetupFiles = ({
	isActive,
	onJourneyStarted,
	sessionId
}: UseFernieSetupFilesOptions): UseFernieSetupFilesReturn => {
	const { wsUserType } = useContext(AuthContext);
	const { data, isLoading } = useGetOnboardingJourneyFilesQuery(undefined, {
		refetchOnMountOrArgChange: true,
		skip: !isActive || wsUserType === "customer"
	});
	const { isLoading: isStandingLoading, standing } = useOnboardingStanding({
		skip: !isActive || wsUserType === "customer"
	});
	const { registerUpload, startJourney, uploadFile } = useOnboardingJourneyMutations();
	const [startedJourneyId, setStartedJourneyId] = useState<string | null>(null);
	const [startedJourneySessionId, setStartedJourneySessionId] = useState<string | null>(null);
	const [attachments, setAttachments] = useState<SetupAttachment[]>([]);
	const [pending, setPending] = useState<PendingSetupRegistration[]>(loadPendingRegistrations);
	const attachedFiles = useRef(new Map<string, File>());
	const registering = useRef(new Set<string>());

	const lookup = data?.onboardingJourneyLookup;
	const unfinishedJourneyId = lookup?.unfinished ? (lookup.journeyId ?? null) : null;
	const openJourneyId = unfinishedJourneyId ?? startedJourneyId;
	const journeySessionReference = unfinishedJourneyId ? (lookup?.sessionReference ?? null) : null;
	const isStartedHere =
		startedJourneyId !== null && startedJourneyId === openJourneyId && startedJourneySessionId === sessionId;
	const isJourneyConversation =
		openJourneyId !== null && sessionId !== null && (sessionId === journeySessionReference || isStartedHere);
	const canStartFromFiles = openJourneyId === null && standing?.isStillOnboarding === true;

	const remember = useCallback(
		(registration: PendingSetupRegistration) => setPending(rememberPendingRegistration(registration)),
		[]
	);
	const forget = useCallback(
		(registration: PendingSetupRegistration) => setPending(forgetPendingRegistration(registration)),
		[]
	);

	useEffect(() => {
		if (startedJourneyId === null || startedJourneySessionId !== null || sessionId === null) return;
		setStartedJourneySessionId(sessionId);
	}, [sessionId, startedJourneyId, startedJourneySessionId]);

	useEffect(() => {
		if (!openJourneyId) return;
		for (const { registration, sessionReference } of registrationsToReplay(
			pending,
			openJourneyId,
			sessionId,
			registering.current
		)) {
			const key = sourceKey(registration);
			registering.current.add(key);
			registerUpload({
				journeyId: registration.journeyId,
				routing: { sessionReference },
				sourceId: registration.sourceId,
				sourceVersion: registration.sourceVersion
			})
				.then((outcome) => {
					if (isReplayableRegistrationOutcome(outcome)) return;
					forget(registration);
					setAttachments((current) => afterRegistration(current, registration, outcome));
				})
				.finally(() => registering.current.delete(key));
		}
	}, [forget, openJourneyId, pending, registerUpload, sessionId]);

	const uploadAttachment = async (id: string, file: File, journeyId: string) => {
		setAttachments((current) => withAttachment(current, id, { failure: undefined, status: "uploading" }));
		const result = await uploadFile(file);
		if (result.error || !result.data) {
			setAttachments((current) =>
				withAttachment(current, id, { failure: describeUploadFailure(result.error), retryable: true, status: "failed" })
			);
			return;
		}
		const { fileName, sourceId, sourceVersion } = result.data;
		setAttachments((current) =>
			withAttachment(current, id, { source: { sourceId, sourceVersion }, status: "uploaded" })
		);
		remember({ fileName, journeyId, sessionReference: sessionId, sourceId, sourceVersion });
	};

	const attachFiles = async (files: File[]) => {
		const journeyId = canStartFromFiles ? await startJourney(sessionId) : openJourneyId;
		if (!journeyId) return;
		if (canStartFromFiles) {
			setStartedJourneyId(journeyId);
			setStartedJourneySessionId(sessionId);
			onJourneyStarted();
		}
		const batch = files.map((file) => ({ attachment: buildSetupAttachment(file, wsId()), file }));
		setAttachments((current) => [...current, ...batch.map(({ attachment }) => attachment)]);
		for (const { attachment, file } of batch) {
			if (attachment.status === "failed") continue;
			attachedFiles.current.set(attachment.id, file);
			await uploadAttachment(attachment.id, file, journeyId);
		}
	};

	const retryAttachment = async (id: string) => {
		const file = attachedFiles.current.get(id);
		if (!file || !openJourneyId) return;
		await uploadAttachment(id, file, openJourneyId);
	};

	const dismissAttachment = (id: string) => {
		attachedFiles.current.delete(id);
		setAttachments((current) => current.filter((attachment) => attachment.id !== id));
	};

	const prepareOutsideUpload = ({ fileName, sourceId, sourceVersion }: OutsideUpload) => {
		if (!openJourneyId) return;
		setAttachments((current) => [
			...current,
			{ fileName, id: wsId(), retryable: false, source: { sourceId, sourceVersion }, status: "uploaded" }
		]);
		remember({ fileName, journeyId: openJourneyId, sessionReference: sessionId, sourceId, sourceVersion });
	};

	const view = openJourneyId
		? buildSetupFilesView(
				attachments,
				pending.filter((registration) => registration.journeyId === openJourneyId),
				data
			)
		: { chips: [], loadedUploads: buildUnrepresentedImportedUploads(data, new Set()), outsideUploads: [] };

	return {
		acceptsSetupFiles: openJourneyId !== null || canStartFromFiles,
		attachFiles,
		chips: view.chips,
		dismissAttachment,
		isJourneyConversation,
		isResolvingJourney: isLoading || isStandingLoading,
		isSetupConversation: openJourneyId !== null,
		journeyId: openJourneyId,
		loadedUploads: view.loadedUploads,
		outsideUploads: view.outsideUploads,
		prepareOutsideUpload,
		retryAttachment
	};
};

export default useFernieSetupFiles;
